import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  Send, 
  MoreHorizontal, 
  RefreshCw, 
  EyeOff,
  Mail,
  Users,
  CheckCircle,
  Clock,
  XCircle,
  Calendar,
  Trophy
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

interface InviteLog {
  id: string;
  invite_type: string;
  status: string;
  entity_type: string;
  entity_name: string;
  entity_id: string;
  created_at: string;
  expires_at: string | null;
  accepted_at: string | null;
  hidden_by_sender: boolean;
  contact: {
    id: string;
    email: string;
    name: string | null;
    first_name: string | null;
    last_name: string | null;
  };
}

const statusConfig: Record<string, { icon: React.ElementType; color: string; label: string }> = {
  pending: { icon: Clock, color: "bg-yellow-100 text-yellow-800", label: "Pending" },
  sent: { icon: Mail, color: "bg-blue-100 text-blue-800", label: "Sent" },
  delivered: { icon: CheckCircle, color: "bg-green-100 text-green-800", label: "Delivered" },
  accepted: { icon: CheckCircle, color: "bg-emerald-100 text-emerald-800", label: "Accepted" },
  expired: { icon: XCircle, color: "bg-gray-100 text-gray-800", label: "Expired" },
  failed: { icon: XCircle, color: "bg-red-100 text-red-800", label: "Failed" },
};

const inviteTypeConfig: Record<string, { icon: React.ElementType; label: string }> = {
  team_invite: { icon: Users, label: "Team Invite" },
  team_invite_create: { icon: Users, label: "Team Lead Invite" },
  event_invite: { icon: Calendar, label: "Event Invite" },
  org_invite: { icon: Users, label: "Organization Invite" },
};

const entityTypeConfig: Record<string, { icon: React.ElementType }> = {
  challenge: { icon: Trophy },
  event: { icon: Calendar },
  organization: { icon: Users },
};

const UserInvites: React.FC = () => {
  const queryClient = useQueryClient();

  // Fetch user's sent invites
  const { data: invites, isLoading } = useQuery({
    queryKey: ["user-invites"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from("invite_logs")
        .select(`
          id, invite_type, status, entity_type, entity_name, entity_id,
          created_at, expires_at, accepted_at, hidden_by_sender,
          contact:platform_contacts!contact_id(id, email, name, first_name, last_name)
        `)
        .eq("sent_by", user.id)
        .eq("hidden_by_sender", false)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching invites:", error);
        throw error;
      }

      return (data || []) as unknown as InviteLog[];
    },
  });

  // Calculate stats
  const stats = React.useMemo(() => {
    if (!invites) return { total: 0, pending: 0, accepted: 0 };
    const total = invites.length;
    const pending = invites.filter(i => ['pending', 'sent', 'delivered'].includes(i.status)).length;
    const accepted = invites.filter(i => i.status === 'accepted').length;
    return { total, pending, accepted };
  }, [invites]);

  // Hide invite mutation
  const hideMutation = useMutation({
    mutationFn: async (inviteId: string) => {
      const { error } = await supabase
        .from("invite_logs")
        .update({ hidden_by_sender: true })
        .eq("id", inviteId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Invite removed from your list");
      queryClient.invalidateQueries({ queryKey: ["user-invites"] });
    },
    onError: () => {
      toast.error("Failed to hide invite");
    },
  });

  // Resend invite mutation
  const resendMutation = useMutation({
    mutationFn: async (invite: InviteLog) => {
      // For team invites, we need to trigger the send-team-invite function
      if (invite.invite_type.includes('team')) {
        // Find the original invitation
        const { data: originalInvite } = await supabase
          .from("team_member_invitations")
          .select("id")
          .eq("invitee_email", invite.contact.email)
          .eq("challenge_id", invite.entity_id)
          .eq("status", "pending")
          .maybeSingle();

        if (!originalInvite) {
          throw new Error("Original invitation not found or already accepted");
        }

        const { error } = await supabase.functions.invoke("send-team-invite", {
          body: { invitation_id: originalInvite.id },
        });

        if (error) throw error;
      } else if (invite.invite_type === 'event_invite') {
        // For event invites
        const { error } = await supabase.functions.invoke("send-event-invite", {
          body: {
            eventId: invite.entity_id,
            eventName: invite.entity_name,
            eventUrl: `${window.location.origin}/events/${invite.entity_id}`,
            emails: [invite.contact.email],
          },
        });

        if (error) throw error;
      }
    },
    onSuccess: () => {
      toast.success("Invite resent successfully");
      queryClient.invalidateQueries({ queryKey: ["user-invites"] });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to resend invite");
    },
  });

  const canResend = (invite: InviteLog) => {
    if (invite.status === 'accepted') return false;
    if (invite.expires_at && new Date(invite.expires_at) < new Date()) return false;
    return true;
  };

  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">My Invites</h1>
        <p className="text-muted-foreground">
          Track invitations you've sent to others
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Sent</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Pending</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">{stats.pending}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Accepted</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-600">{stats.accepted}</div>
          </CardContent>
        </Card>
      </div>

      {/* Invites Table */}
      <Card>
        <CardHeader>
          <CardTitle>Sent Invitations</CardTitle>
          <CardDescription>
            All invitations you've sent to team members and events
          </CardDescription>
        </CardHeader>
        <CardContent>
          {invites && invites.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Recipient</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>For</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Sent</TableHead>
                  <TableHead className="w-[80px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invites.map((invite) => {
                  const StatusIcon = statusConfig[invite.status]?.icon || Clock;
                  const TypeConfig = inviteTypeConfig[invite.invite_type] || { icon: Mail, label: invite.invite_type };
                  const EntityIcon = entityTypeConfig[invite.entity_type]?.icon || Mail;

                  return (
                    <TableRow key={invite.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">
                            {invite.contact?.first_name || invite.contact?.name || "Unknown"}
                            {invite.contact?.last_name && ` ${invite.contact.last_name}`}
                          </div>
                          <div className="text-sm text-muted-foreground">{invite.contact?.email}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <TypeConfig.icon className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">{TypeConfig.label}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <EntityIcon className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm truncate max-w-[150px]">{invite.entity_name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={statusConfig[invite.status]?.color || "bg-gray-100"}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {statusConfig[invite.status]?.label || invite.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {format(new Date(invite.created_at), "MMM d, yyyy")}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {canResend(invite) && (
                              <DropdownMenuItem
                                onClick={() => resendMutation.mutate(invite)}
                                disabled={resendMutation.isPending}
                              >
                                <RefreshCw className="h-4 w-4 mr-2" />
                                Resend
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                              onClick={() => hideMutation.mutate(invite.id)}
                              disabled={hideMutation.isPending}
                              className="text-destructive"
                            >
                              <EyeOff className="h-4 w-4 mr-2" />
                              Remove from list
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-12">
              <Send className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium">No invites yet</h3>
              <p className="text-muted-foreground mb-4">
                When you invite others to join teams or events, they'll appear here.
              </p>
              <Button variant="outline" onClick={() => window.location.href = '/challenges'}>
                Browse Challenges
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default UserInvites;